const canonical = value => Array.isArray(value) ? value.map(canonical)
  : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;

export function branchIdeaContent(node) {
  const fields = ['color', 'fontSize', 'fontFamily', 'linkUrl', 'linkMeta', 'embeddedMindmapId', 'attachment',
    'userId', 'creatorName', 'responsiblePrincipal', 'responsibilityHistory', 'taskCompletionHistory', 'emoji', 'tags'];
  return JSON.stringify(canonical({
    text: node.text || '', info: node.info || '', isTask: Boolean(node.isTask), taskDone: Boolean(node.taskDone),
    isEmbeddedMindmapLink: Boolean(node.isEmbeddedMindmapLink),
    ...Object.fromEntries(fields.map(key => [key, node[key] ?? null])),
  }));
}

export function planBranchMap({ nodeId, nodes, relationships, actorUserId, meta = {} }) {
  const byId = new Map(nodes.map(node => [node.id, node]));
  const root = byId.get(nodeId);
  if (!root) throw new Error('The selected idea is no longer available.');
  const children = new Map();
  for (const edge of relationships) {
    if (!children.has(edge.parentId)) children.set(edge.parentId, []);
    children.get(edge.parentId).push(edge.childId);
  }
  const ids = new Set();
  const queue = [nodeId];
  while (queue.length) {
    const id = queue.pop();
    if (ids.has(id)) throw new Error('This branch contains a cycle.');
    if (!byId.has(id)) throw new Error('A branch idea is no longer available.');
    ids.add(id);
    queue.push(...(children.get(id) || []));
  }
  if (ids.size < 2) throw new Error('Choose an idea with children.');
  const branchNodes = [...ids].map(id => byId.get(id));
  if (branchNodes.some(node => node.userId && node.userId !== actorUserId)) {
    throw new Error('You can only move branches containing ideas you created.');
  }
  const branchRelationships = relationships.filter(edge => ids.has(edge.parentId) || ids.has(edge.childId));
  const graph = {
    nodes: branchNodes.map(node => ({ ...node, isRoot: node.id === nodeId, collapsedChildren: null,
      position: { x: node.position.x - root.position.x + 300, y: node.position.y - root.position.y + 300 } })),
    relationships: branchRelationships.filter(edge => ids.has(edge.parentId) && ids.has(edge.childId)),
    root: nodeId, selectedId: nodeId, meta,
  };
  return { nodeId, title: root.text, branchNodeIds: [...ids], branchRelationships,
    expectedContent: branchNodes.map(node => ({ id: node.id, content: branchIdeaContent(node) })), graph };
}

export async function convertBranchToMap({ plan, create, replace, buildUrl }) {
  const created = await create(plan.graph);
  if (!created?.id) throw new Error('The new map could not be saved. The original branch is unchanged.');
  try {
    const applied = replace({ ...plan, linkedMapId: created.id, nodePatch: {
      text: plan.title, embeddedMindmapId: created.id, isEmbeddedMindmapLink: true,
      linkUrl: buildUrl(created.id), linkMeta: { title: plan.title, movedIdeaIds: plan.branchNodeIds },
      attachment: null, collapsedChildren: null,
    } });
    if (!applied) throw new Error('The branch could not be replaced.');
    return created;
  } catch (error) {
    error.createdMindmapId = created.id;
    throw error;
  }
}

export function waitForMapSync(provider, timeoutMs = 15000) {
  if (!provider || !provider.hasUnsyncedChanges) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => { clearTimeout(timer); provider.off('unsyncedChanges', onChange); };
    const onChange = () => { if (!provider.hasUnsyncedChanges) { cleanup(); resolve(); } };
    const timer = setTimeout(() => { cleanup(); reject(new Error('The map is still syncing. Wait for the connection to recover before continuing.')); }, timeoutMs);
    provider.on('unsyncedChanges', onChange);
    onChange();
  });
}
